import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDonors, deleteDonor } from "../../redux/donorSlice";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { IconButton, Button, Modal, Box, Typography } from "@mui/material";
import { filterDonors } from "./utils/DonorUtils";
import { getHeaderClass } from "../../utils/agGridHelpers";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import Filter from "../Filter";
import AgGridLoader from "../AgGridLoader";
import ConfirmationDialog from "../ConfirmationDialog";
import AlertMessage from "../AlertMessage";
import { fetchDonation } from "../../redux/donationSlice";
import { getSystemFormattedTimestamp } from "../../utils/dateFormatted";
import { clearAllFilters } from "../../utils/agGridHelpers";
import { logger } from "../../utils/logger";
import { useDebouncedLogger } from "../../hooks/useDebouncedLogger";
import SnackBarAlert from "../SnackBarAlert";
import HistoryModal from "../HistoryModal";



import * as XLSX from "xlsx";
import { saveAs } from "file-saver";



const DonorList = ({ onEdit, openDonorForm }) => {
  const dispatch = useDispatch();
  const { donors, loading } = useSelector((state) => state.donor || {});
  const { role } = useSelector((state) => state.auth || "user");
  const { user } = useSelector((state) => state.auth);
  const logFilter = useDebouncedLogger();
  const { donations, loading: donationsLoading, status: donationsStatus } = useSelector((state) => state.donation || {});
  const donationsReady = donationsStatus === "succeeded";
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedDonorHasDonations, setSelectedDonorHasDonations] =
    useState(false);
  const [alertMessageOpen, setAlertMessageOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState(
    "This donor has existing donations linked to their record. Please remove the donations before deleting the donor."
  );
  const [gridReady, setGridReady] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const gridRef = useRef(null);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState([]);
  const [selectedDonorId, setSelectedDonorId] = useState(null);
  const [filterValues, setFilterValues] = useState({});
const [isDonationFetching, setIsDonationFetching] = useState(true);
  const filteredDonors = useMemo(
    () => filterDonors(donors, filterValues),
    [donors, filterValues]
  );
const donorIdsWithDonations = useMemo(() => {
  if (!donations?.length) return new Set();
  return new Set(donations.map((d) => String(d.donorId)));
}, [donations]);
  useEffect(() => {
    if (!gridReady) return;

    const fetchAll = async () => {
      if (!donors || donors.length === 0) {
        dispatch(fetchDonors());
        setInitialFetchDone(true);
        }
      // ✅ donations fetch donors-ஓட condition-க்கு outside-ல பண்ணணும்
      if (!donations || donations.length === 0) {
  setIsDonationFetching(true);
  await dispatch(fetchDonation()).finally(() => {
    setIsDonationFetching(false);
  });
} else {
  setIsDonationFetching(false);
}
    };

    fetchAll();
  }, [dispatch, gridReady]);

  const onGridReady = (params) => {
    gridRef.current = params.api;
    setGridReady(true);
  };

  useEffect(() => {
    if (!gridReady || !gridRef.current) return;
    if (loading) {
      gridRef.current.setGridOption?.("loading", true);
    } else if (!loading && filteredDonors.length === 0 && initialFetchDone) {
      gridRef.current.setGridOption?.("loading", false);
      gridRef.current.showNoRowsOverlay();
    } else {
      gridRef.current.setGridOption?.("loading", false);
      gridRef.current.hideOverlay();
    }
  }, [loading, filteredDonors, gridReady, initialFetchDone]);

  const handleFilterChange = (filters) => {
    setFilterValues(filters);
    // Debounced — logs the settled filter state ~700ms after the user
    // stops typing, instead of once per keystroke.
    logFilter("donorFilter", "Donor list filter applied", {
      page: "Donors",
      username: user?.username,
      role: user?.role,
      appliedFilters: filters,
    });
  };

  const handleClearAll = () => {
    clearAllFilters(gridRef.current);
  };

  const handleViewHistory = (donor) => {
    if (donor.updateHistory && donor.updateHistory.length > 0) {
      setSelectedHistory(donor.updateHistory);
      setSelectedDonorId(donor.donorId);
      setHistoryModalOpen(true);
      logger.info("View donor updated history modal opened", {
        page: "Donors",
        donorId: donor.donorId,
        username: user?.username,
        role: user?.role,
      });
    } else {
      setFeedback({
        open: true,
         message: "No update history found for this donor.",
        severity: "info",
      });
      logger.info("No donor history found", {
        page: "Donors",
        donorId: donor.donorId,
        username: user?.username,
        role: user?.role,
      });
    }
  };

  const handleCloseHistory = () => {
    logger.info("View donor history modal closed", {
      page: "Donors",
      username: user?.username,
      role: user?.role,
    });
    setHistoryModalOpen(false);
    setSelectedHistory([]);
    setSelectedDonorId(null);
  };

  const handleEdit = (data) => {
    logger.info("Edit donor clicked", {
      page: "Donors",
      donorId: data.donorId,
      username: user?.username,
      role: user?.role,
    });
    onEdit(data);
  };

  const handleDeleteClick = (donor) => {
    if (!donationsReady) return;

    setSelectedDonor(donor);

   // ✅ Donations இன்னும் load ஆகலன்னா delete allow பண்ணாதே
    if (isDonationFetching) {
      logger.warn("Delete attempted while donations still loading", {
        donorId: donor.donorId,
        username: user?.username,
        role: user?.role,
      });
      setFeedback({
        open: true,
       message: "Donation records are still loading. Please wait a moment and try again.",
        severity: "warning",
      });
      return;
    }

    const hasDonations = donations?.some(
      (donation) => String(donation.donorId) === String(donor.donorId)
    );

    setSelectedDonorHasDonations(hasDonations);

    if (hasDonations) {
       setAlertMessage(
        "This donor has existing donations linked to their record. Please remove the donations before deleting the donor."
      );
       setAlertMessageOpen(true);
      logger.warn("Attempted to delete donor with donations", {
        page: "Donors",
        donorId: donor.donorId,
        username: user?.username,
        role: user?.role,
         
      });
           
   
    } else {
      logger.info("Delete confirmation opened for donor", {
        page: "Donors",
        donorId: donor.donorId,
        username: user?.username,
        role: user?.role,
      });
      setOpenConfirm(true);
    }
  };

  const handleCloseFeedback = () => {
    setFeedback({ ...feedback, open: false });
  };

  // const handleDeleteConfirm = () => {
  //   if (selectedDonor) {
  //     dispatch(deleteDonor(selectedDonor._id))
  //       .unwrap()
  //       .then(() => {
  //         setFeedback({
  //           open: true,
  //           message: "Donor deleted successfully",
  //           severity: "success",
  //         });
  //         logger.info("Donor deleted successfully", {
  //           donorId: selectedDonor.donorId,
  //           username: user?.username,
  //           role: user?.role,
  //         });
  //       })
  //       .catch((err) => {
  //         setFeedback({
  //           open: true,
  //           message: "Error deleting donor",
  //           severity: "error",
  //         });
  //         logger.error("Error deleting donor", {
  //           donorId: selectedDonor.donorId,
  //           username: user?.username,
  //           role: user?.role,
  //           error: err?.message || err,
  //         });
  //       });
  //   }
  //   setOpenConfirm(false);
  //   setSelectedDonor(null);
  // };

   const handleDeleteConfirm = () => {
    if (selectedDonor) {
      // CHANGE 5: YES click பண்ணிய பிறகும் ஒரு முறை re-check பண்ணு
      // Confirmation dialog open ஆன பிறகு யாராவது donation add பண்ணிருந்தாலும் catch ஆகும்
      const reCheckHasDonations = donations?.some(
        (donation) => String(donation.donorId) === String(selectedDonor.donorId)
      );

      if (reCheckHasDonations) {
        logger.warn("Re-check blocked delete — donor now has donations", {
          donorId: selectedDonor.donorId,
          username: user?.username,
          role: user?.role,
        });
        setAlertMessage(
          "A donation was recently added to this donor. Deletion has been cancelled to protect the records."
        );
        setAlertMessageOpen(true);
        setOpenConfirm(false);
        setSelectedDonor(null);
        return;
      }

      dispatch(deleteDonor(selectedDonor._id))
        .unwrap()
        .then(() => {
          setFeedback({
            open: true,
            message: "Donor has been deleted successfully.",
            severity: "success",
          });
          logger.info("Donor deleted successfully", {
            page: "Donors",
            donorId: selectedDonor.donorId,
            username: user?.username,
            role: user?.role,
          });
        })
        // CHANGE 6: Backend error response handle — donations conflict vs other errors
        .catch((err) => {
          setFeedback({
            open: true,
            message: "Error deleting donor",
            severity: "error",
          });
          logger.error("Error deleting donor", {
            page: "Donors",
            donorId: selectedDonor.donorId,
            username: user?.username,
            role: user?.role,
            error: err?.message || err,
          });
          const backendMessage =
            err?.message || err?.data?.message || "Error deleting donor";

          const isDonationConflict =
            typeof backendMessage === "string" &&
            backendMessage.toLowerCase().includes("donation");

          if (isDonationConflict) {
            // Backend-ல donations இருக்குன்னு block பண்ணா → AlertMessage காட்டு
            logger.warn("Backend blocked delete — donor has donations", {
              donorId: selectedDonor.donorId,
              username: user?.username,
              role: user?.role,
              backendMessage,
            });
            setAlertMessage(
              "This donor has existing donations linked to their record. Please remove the donations before deleting the donor."
            );
            setAlertMessageOpen(true);
          } else {
            // Other server errors → SnackBar-ல error காட்டு
            logger.error("Error deleting donor", {
              donorId: selectedDonor.donorId,
              username: user?.username,
              role: user?.role,
              error: backendMessage,
            });
            setFeedback({
              open: true,
              message: "Something went wrong while deleting the donor. Please try again or contact support.",
              severity: "error",
            });
          }
        });
    }
    setOpenConfirm(false);
    setSelectedDonor(null);
  };


  const handleCloseConfirm = () => {
       
    setOpenConfirm(false);
    setSelectedDonor(null);
  };

  const handleRowDoubleClick = (params) => {
    const selectedDonor = params.data;
    logger.info("Donor row double-clicked", {
      page: "Donors",
      donorId: selectedDonor.donorId,
      username: user?.username,
      role: user?.role,
    });

    openDonorForm(selectedDonor);
    if (gridRef.current) {
      gridRef.current.setFilterModel({
        donorId: selectedDonor?.donorId,
      });
    }
  };

  const defaultColDef = useMemo(
  () => ({
    filter: "agTextColumnFilter",
    filterParams: {
      buttons: ["apply", "reset"],
      closeOnApply: true,
    },
  }),
  []
);
  const columnDefs = [
    {
      field: "_id",
      hide: true,
      headerName: "ID",
      valueGetter: (params) => params.data?._id || "-",
      headerClass: getHeaderClass,
    },
    {
      headerName: "Actions",
      Filter: false,
      cellRenderer: (params) => {
        if (!params.data) return null;
        const donorData = params.data;
        // const hasDonations = donations.some(
        //   (donation) => String(donation.donorId) === String(donorData.donorId)
        // );

       const hasDonations = isDonationFetching
  ? false
  : donorIdsWithDonations.has(String(donorData.donorId));

        return (
          <div style={{ display: "flex", gap: "10px" }}>
            {(role === "admin1" ||
              role === "admin2" ||
              role === "superadmin" ||
              role === "editor1" ||
              role === "editor2") && (
              <IconButton
                onClick={() => handleEdit(donorData)}
                title="Edit"
                style={{ color: "#1976d2" }}
                aria-label="Edit Donor"
                data-log="Edit Icon Button"
              >
                <EditIcon />
              </IconButton>
            )}

            {(role === "superadmin" ||
              role === "admin1" ||
              role === "admin2") && (
              <IconButton
                onClick={() => handleDeleteClick(donorData)}
                disabled={!donationsReady}
                title={
                  !donationsReady
                    ? "Loading donations, please wait..."
                    : hasDonations
                    ? "Cannot delete: donor has donations"
                    : "Delete donor"
                }
                style={{
                  color: !donationsReady || hasDonations ? "gray" : "#d32f2f",
                  cursor: !donationsReady ? "not-allowed" : "pointer",
                }}
                data-log="Delete Icon Button"
              >
                <DeleteIcon />
              </IconButton>
            )}
            {(role === "admin1" ||
              role === "superadmin" ||
              role === "admin2") && (
              <IconButton
                onClick={() => handleViewHistory(donorData)}
                title="View Update History"
                style={{ color: "#1976D2" }}
                aria-label="View Update History"
                data-log="View History Icon Button"
              >
                <HistoryIcon />
              </IconButton>
            )}
          </div>
        );
      },
    },
    {
      field: "id",
      headerName: "S.No",
        filter: false,
      valueGetter: (params) => params.node.rowIndex + 1,
      width: 90,
      headerClass: getHeaderClass,
    },
    {
      field: "donorId",
      headerName: "Donor ID",
      filter: "agTextColumnFilter",
     
      
      valueGetter: (params) => params.data?.donorId || "-",
      width: 100,
      headerClass: getHeaderClass,
    },
    {
      field: "salutation",
      headerName: "Salutation",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.salutation || "-",
      width: 90,

      headerClass: getHeaderClass,
    },
    {
      field: "initial",
      headerName: "Initial",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.initial || "-",
      width: 90,
      headerClass: getHeaderClass,
    },
    {
      field: "firstName",
      headerName: "First Name",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.firstName || "-",
      width: 150,
      headerClass: getHeaderClass,
    },
    {
      field: "lastName",
      headerName: "Last Name",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.lastName || "-",
      width: 150,
      headerClass: getHeaderClass,
    },
    {
      field: "address",
      headerName: "Address",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.address || "-",
      width: 110,
      headerClass: getHeaderClass,
    },
    {
      field: "address1",
      headerName: "Address1",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.address1 || "-",
      width: 110,
      headerClass: getHeaderClass,
    },
    {
      field: "address2",
      headerName: "Address2",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.address2 || "-",
      width: 110,
      headerClass: getHeaderClass,
    },
    {
      field: "city",
      headerName: "City",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.city || "-",
      width: 110,
      headerClass: getHeaderClass,
    },
    {
      field: "state",
      headerName: "State",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.state || "-",
      width: 110,
      headerClass: getHeaderClass,
    },
    {
      field: "country",
      headerName: "Country",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.country || "-",
      width: 110,
      headerClass: getHeaderClass,
    },
    {
      field: "pincode",
      headerName: "Pincode",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.pincode || "-",
      width: 100,
      headerClass: getHeaderClass,
    },
    {
      field: "panCardNo",
      headerName: "PAN No",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.panCardNo || "-",
      width: 180,
      headerClass: getHeaderClass,
    },
    {
      field: "mobile1",
      headerName: "Mobile1",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.mobile1 || "-",
      width: 120,
      headerClass: getHeaderClass,
      filterParams: {
         buttons: ["apply", "reset"],   // 👈 add
  closeOnApply: true, 
        maxNumConditions: 1,
        trimInput: true,

        textMatcher: ({ filterText, value, filterOption }) => {
          const rawValue = value ?? "";
          const valueDigits = rawValue.replace(/\D/g, "");

          // BLANK / NOT BLANK
          if (filterOption === "blank") {
            return valueDigits.length === 0;
          }
          if (filterOption === "notBlank") {
            return valueDigits.length > 0;
          }

          // If no filter text, allow row
          if (!filterText) return true;

          const filterDigits = filterText.replace(/\D/g, "");
          if (filterDigits.length < 3) return true;

          switch (filterOption) {
            case "contains":
              return valueDigits.includes(filterDigits);

            case "notContains":
              return !valueDigits.includes(filterDigits);

            case "equals":
              return valueDigits === filterDigits;

            case "notEqual":
              return valueDigits !== filterDigits;

            case "startsWith":
              return valueDigits.startsWith(filterDigits);

            case "endsWith":
              return valueDigits.endsWith(filterDigits);

            default:
              return true;
          }
        },
      },
    },
    {
      field: "mobile2",
      headerName: "Mobile2",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.mobile2 || "-",
      width: 120,
      headerClass: getHeaderClass,
      filterParams: {
        maxNumConditions: 1,
        trimInput: true,

        textMatcher: ({ filterText, value, filterOption }) => {
          const rawValue = value ?? "";
          const valueDigits = rawValue.replace(/\D/g, "");

          // BLANK / NOT BLANK
          if (filterOption === "blank") {
            return valueDigits.length === 0;
          }
          if (filterOption === "notBlank") {
            return valueDigits.length > 0;
          }

          // If no filter text, allow row
          if (!filterText) return true;

          const filterDigits = filterText.replace(/\D/g, "");
          if (filterDigits.length < 3) return true;

          switch (filterOption) {
            case "contains":
              return valueDigits.includes(filterDigits);

            case "notContains":
              return !valueDigits.includes(filterDigits);

            case "equals":
              return valueDigits === filterDigits;

            case "notEqual":
              return valueDigits !== filterDigits;

            case "startsWith":
              return valueDigits.startsWith(filterDigits);

            case "endsWith":
              return valueDigits.endsWith(filterDigits);

            default:
              return true;
          }
        },
      },
    },
    {
      field: "mailId",
      headerName: "MailId",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.mailId || "-",
      width: 150,
      headerClass: getHeaderClass,
    },
    {
      field: "status",
      headerName: "Status",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.status || "-",
      width: 100,
      headerClass: getHeaderClass,
    },
    {
      field: "prasad",
      headerName: "Prasad",
      filter: "agTextColumnFilter",
      valueGetter: (params) =>
        params.data?.prasad === undefined || params.data?.prasad === null
          ? "-"
          : params.data.prasad
          ? "Yes"
          : "No",
      width: 80,
      headerClass: getHeaderClass,
    },
    {
      field: "donorRemarks",
      headerName: "Donor Remarks",
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data?.donorRemarks || "-",
      width: 150,
      headerClass: getHeaderClass,
    },
  ];

  const onFirstDataRendered = () => {
    setIsFirstLoad(false);
    // Ensure overlay is hidden when data renders
    if (gridRef.current && donors && donors.length > 0) {
      gridRef.current.hideOverlay();
    }
  };

const exportToExcel = () => {
  if (!gridRef.current) return;
  const rowData = [];
  gridRef.current.forEachNodeAfterFilterAndSort((node) => {
    if (node.data) rowData.push(node.data);
  });
  const ws = XLSX.utils.json_to_sheet(rowData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Donors");
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(
    new Blob([buf], { type: "application/octet-stream" }),
    `Donors_export_${getSystemFormattedTimestamp()}.xlsx`
  );
};

//   gridRef.current.forEachNodeAfterFilterAndSort((node) => {
//     if (node.data) rowData.push(node.data);
//   });

//   const doc = new jsPDF({ orientation: "landscape" });

//   doc.setFontSize(14);
//   doc.text("Donors List", 14, 15);

//   autoTable(doc, {
//     startY: 22,
//     head: [[
//       "Donor ID", "Salutation", "First Name", "Last Name",
//       "Address", "City", "State", "Pincode",
//       "Mobile1", "Mobile2", "PAN No", "Status"
//     ]],
//     body: rowData.map((d) => [
//       d.donorId || "-",
//       d.salutation || "-",
//       d.firstName || "-",
//       d.lastName || "-",
//       d.address || "-",
//       d.city || "-",
//       d.state || "-",
//       d.pincode || "-",
//       d.mobile1 || "-",
//       d.mobile2 || "-",
//       d.panCardNo || "-",
//       d.status || "-",
//     ]),
//     styles: { fontSize: 7 },
//     headStyles: { fillColor: [25, 118, 210] },
//   });

//   doc.save(`Donors_export_${getSystemFormattedTimestamp()}.pdf`);
// };

// const exportToPDF = async () => {
//   setPdfLoading(true);
//   try {
//     const BaseUrl = process.env.REACT_APP_BASE_URL;
//     const state = store.getState();
//     const token = state?.auth?.token || sessionStorage.getItem("accessToken");

//     const response = await fetch(`${BaseUrl}api/donors/export-pdf`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${token}`,
//       },
//     });

//     if (!response.ok) throw new Error("Failed to generate PDF");

//     const blob = await response.blob();
//     const url = window.URL.createObjectURL(blob);
//     const link = document.createElement("a");
//     link.href = url;
//     link.download = `Donors_export_${getSystemFormattedTimestamp()}.pdf`;
//     document.body.appendChild(link);
//     link.click();
//     link.remove();
//     window.URL.revokeObjectURL(url);
//   } catch (err) {
//     alert("Error downloading PDF: " + err.message);
//   }
//   finally {
//     setPdfLoading(false);
//   }
// };  
return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 200px)",
      }}
    >
      <Filter
        onFilterChange={handleFilterChange}
        fields={["donorId", "name", "mobile", "pincode", "status", "remarks"]}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "end",
          marginBottom: "10px",
        }}
      >
        <Button
          variant="contained"
          color="error"
          onClick={handleClearAll}
          sx={{ mr: 2 }}
        >
          Clear All Filters
        </Button>
       {role === "superadmin" || role === "admin1" || role === "admin2" ? (
          <Button onClick={exportToExcel} variant="contained" color="primary">
            <DriveFileMoveIcon /> Export
          </Button>
        ) : null}
         

      </div>
      <div
        className="ag-theme-alpine"
        style={{ height: "100%", width: "100%", overflow: "auto" }}
      >
        <HistoryModal
          title="Donor Updated History"
          open={historyModalOpen}
          onClose={handleCloseHistory}
          history={selectedHistory}
          donorId={selectedDonorId}
        />

        <AgGridReact
          ref={gridRef}
          rowData={filteredDonors}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          domLayout="autoHeight"
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          pagination
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 20, 50]}
          loadingOverlayComponent={AgGridLoader}
          loadingOverlayComponentParams={{
            loadingMessage: "Loading donors, please wait...",
          }}
          onRowDoubleClicked={handleRowDoubleClick}
          onFilterChanged={(params) => params.api.refreshHeader()}
        />

        <AlertMessage
          title="Donor Delete Alert"
          open={alertMessageOpen}
          setOpen={setAlertMessageOpen}
          message={alertMessage}
        />

        <ConfirmationDialog
          open={openConfirm}
          title="Delete Donor"
          message="Are you sure you want to delete this donor?"
          onConfirm={handleDeleteConfirm}
          onCancel={handleCloseConfirm}
        />
        <SnackBarAlert feedback={feedback} onClose={handleCloseFeedback} />
      </div>
    </div>
  );
};

export default DonorList;
